import { Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { keys, omit, pick } from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { p2pProvidersApi, usersApi } from 'api';
import {
  AgentsFormFields,
  CommentField,
  EntityDetailsPage,
  EntityDetailsPageChildrenProps,
  FormikNumericField,
  FormikSelect,
  FormikTextField,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import {
  useCurrencies,
  useP2PProvidersConfig,
  useUser,
  useMeta,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  P2PProvider,
  P2PProviderDto,
  P2PProviderFormValues,
  PayoutResponseTemplatesFormValues,
} from 'types';
import { withNormalizedPayinFlowMode } from 'utils';

import { P2P_PROVIDER_INITIAL_VALUE } from './constants';
import { P2PProviderPayinConfig } from './P2PProviderPayinConfig';
import { P2PProviderPayoutConfig } from './P2PProviderPayoutConfig';
import { P2PProviderPayoutLimits } from './P2PProviderPayoutLimits';
import {
  getP2PProviderValidationSchema,
  parseCommaSeparatedList,
  parseErrorIndicators,
} from './utils';

export const P2PProviderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = useMemo(() => id === NEW_ID, [id]);

  const {
    assetCurrenciesOptions,
    fiatCurrenciesOptions,
    defaultAssetCurrency,
    getAssetCurrencySymbol,
    getCurrencyExchangeOptions,
    getAssetCurrencyExchangeOptions,
  } = useCurrencies();

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const { isTechOperator, isAdmin } = useUser();
  const { allTradeMethods } = useUserContext();
  const queryResultTraders = useQuery(
    QueryKey.UsersTraders,
    usersApi.getAllTraders,
  );

  const title = useMemo(
    () => (isNew ? t('details.create_title') : t('details.edit_title')),
    [t, isNew],
  );

  const listUrl = useMemo(() => {
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.P2P_PROVIDERS;
    } else if (isAdmin) {
      return ROUTE_PATH.ADMIN.P2P_PROVIDERS;
    }
  }, [isAdmin, isTechOperator]);

  const initialValues = useMemo(
    (): P2PProviderFormValues => P2P_PROVIDER_INITIAL_VALUE,
    [],
  );

  const { availableP2PProviderTypeOptions } = useP2PProvidersConfig();
  const {
    isDynamicAdaptersEnabled,
    isProviderAgentsEnabled,
    isZeroFeesForbidden,
  } = useMeta();

  const typeOptions = useMemo(
    () =>
      isDynamicAdaptersEnabled
        ? availableP2PProviderTypeOptions
        : availableP2PProviderTypeOptions.filter((opt) => opt.isSystem),
    [isDynamicAdaptersEnabled, availableP2PProviderTypeOptions],
  );

  const validTypeCodes = useMemo(
    () => typeOptions.map((opt) => opt.value),
    [typeOptions],
  );

  const validationSchema: Yup.ObjectSchema<P2PProviderFormValues> = useMemo(
    () =>
      getP2PProviderValidationSchema(
        defaultAssetCurrency?.id,
        allTradeMethods,
        validTypeCodes,
        isZeroFeesForbidden,
      ),
    [
      defaultAssetCurrency?.id,
      allTradeMethods,
      validTypeCodes,
      isZeroFeesForbidden,
    ],
  );

  const handlePayinTradeMethodsChange = useCallback(
    (event: any, formik: FormikProps<P2PProviderDto>) => {
      const newSelectedTradeMethods = event.target.value;

      formik.setFieldValue(
        `payinConfig.tradeMethods`,
        newSelectedTradeMethods.map((id: string) => ({ id })),
      );
    },
    [],
  );

  const handlePayoutTradeMethodsChange = useCallback(
    (event: any, formik: FormikProps<P2PProviderDto>) => {
      const newSelectedTradeMethods = event.target.value;

      formik.setFieldValue(
        `payoutConfig.tradeMethods`,
        newSelectedTradeMethods.map((id: string) => ({ id })),
      );
    },
    [],
  );

  const resetCurrencyRelatedFields = useCallback(
    (formik: FormikProps<P2PProviderDto>) => {
      formik.setFieldValue('currencyExchangeId', '');
      formik.setFieldTouched('currencyExchangeId', true);

      formik.setFieldValue('assetCurrencyExchangeId', '');
      formik.setFieldTouched('assetCurrencyExchangeId', true);
    },
    [],
  );

  const handleFiatCurrencyChange = useCallback(
    (formik: FormikProps<P2PProviderDto>) => {
      formik.setFieldValue('payinConfig.tradeMethods', []);
      formik.setFieldValue('payoutConfig.tradeMethods', []);
      resetCurrencyRelatedFields(formik);
    },
    [resetCurrencyRelatedFields],
  );

  const handleAssetCurrencyChange = useCallback(
    (formik: FormikProps<P2PProviderDto>) => {
      resetCurrencyRelatedFields(formik);
    },
    [resetCurrencyRelatedFields],
  );

  const initialValuesSelect = useCallback(
    (data: P2PProvider) => {
      const pickedData = pick(data, keys(initialValues));
      pickedData.payinConfig = data.payinConfig
        ? pick(data.payinConfig, keys(initialValues.payinConfig))
        : initialValues.payinConfig;
      if (pickedData.payinConfig) {
        pickedData.payinConfig.intervalCommissions =
          pickedData.payinConfig.intervalCommissions || [];
        pickedData.payinConfig.ignoreWebhook =
          pickedData.payinConfig.ignoreWebhook ?? false;
        pickedData.payinConfig.credentials = withNormalizedPayinFlowMode(
          data.type,
          pickedData.payinConfig.credentials,
        );
      }
      pickedData.payoutConfig = data.payoutConfig
        ? pick(data.payoutConfig, keys(initialValues.payoutConfig))
        : (initialValues.payoutConfig as typeof pickedData.payoutConfig);
      if (pickedData.payoutConfig) {
        pickedData.payoutConfig.ignoreWebhook =
          pickedData.payoutConfig.ignoreWebhook ?? false;
        // Always materialise a complete form-shaped templates object, even when the
        // provider has no templates yet. Otherwise the nested object stays undefined and
        // the response-template fields render uncontrolled — their values only reach Formik
        // state once the user *touches* them, so an untouched edit drops the config on save.
        const responseTemplates =
          pickedData.payoutConfig.payoutResponseTemplates;
        const formTemplates: PayoutResponseTemplatesFormValues = {
          statusFieldPath: responseTemplates?.statusFieldPath || '',
          acceptedStatuses: (responseTemplates?.acceptedStatuses || []).join(
            ', ',
          ),
          rejectedStatuses: (responseTemplates?.rejectedStatuses || []).join(
            ', ',
          ),
          requiredResponseFields: (
            responseTemplates?.requiredResponseFields || []
          ).join(', '),
          rejectedHttpStatusCodes: (
            responseTemplates?.rejectedHttpStatusCodes || []
          ).join(', '),
          errorIndicators: responseTemplates?.errorIndicators?.length
            ? JSON.stringify(responseTemplates.errorIndicators, null, 2)
            : '',
          acceptanceIndicators: responseTemplates?.acceptanceIndicators?.length
            ? JSON.stringify(responseTemplates.acceptanceIndicators, null, 2)
            : '',
        };
        (
          pickedData.payoutConfig as Record<string, unknown>
        ).payoutResponseTemplates = formTemplates;
      }
      pickedData.payoutLimits = data.payoutLimits
        ? pick(data.payoutLimits, keys(initialValues.payoutLimits))
        : initialValues.payoutLimits;
      pickedData.agents = data.agents || [];
      pickedData.comment = data.comment ?? '';
      (pickedData as P2PProvider).commentedAt = data.commentedAt;
      (pickedData as P2PProvider).commentedBy = data.commentedBy;
      return pickedData;
    },
    [initialValues],
  );

  const formatCurrencySymbol = useCallback(
    (assetCurrencyId?: string) =>
      assetCurrencyId
        ? getAssetCurrencySymbol(assetCurrencyId)
        : defaultAssetCurrency?.symbol,
    [defaultAssetCurrency?.symbol, getAssetCurrencySymbol],
  );

  const convertPayoutConfig = useCallback(
    (payoutConfig: P2PProviderFormValues['payoutConfig']) => {
      const formTemplates = payoutConfig?.payoutResponseTemplates;
      const statusFieldPath = formTemplates?.statusFieldPath?.trim() || '';
      const acceptedStatuses = parseCommaSeparatedList(
        formTemplates?.acceptedStatuses,
      );
      const rejectedStatuses = parseCommaSeparatedList(
        formTemplates?.rejectedStatuses,
      );
      const requiredResponseFields = parseCommaSeparatedList(
        formTemplates?.requiredResponseFields,
      );
      const rejectedHttpStatusCodes = parseCommaSeparatedList(
        formTemplates?.rejectedHttpStatusCodes,
      )
        .map(Number)
        .filter((code) => !isNaN(code));
      const errorIndicators = parseErrorIndicators(
        formTemplates?.errorIndicators,
      );
      const acceptanceIndicators = parseErrorIndicators(
        formTemplates?.acceptanceIndicators,
      );
      const hasTemplates =
        !!statusFieldPath ||
        acceptedStatuses.length > 0 ||
        rejectedStatuses.length > 0 ||
        requiredResponseFields.length > 0 ||
        rejectedHttpStatusCodes.length > 0 ||
        errorIndicators.length > 0 ||
        acceptanceIndicators.length > 0;
      return payoutConfig
        ? {
            ...payoutConfig,
            payoutResponseTemplates: hasTemplates
              ? {
                  statusFieldPath,
                  acceptedStatuses,
                  rejectedStatuses,
                  requiredResponseFields,
                  rejectedHttpStatusCodes,
                  ...(errorIndicators.length > 0 && { errorIndicators }),
                  ...(acceptanceIndicators.length > 0 && {
                    acceptanceIndicators,
                  }),
                }
              : undefined,
          }
        : undefined;
    },
    [],
  );

  const onBeforeCreateMutation = useCallback(
    (data: P2PProviderFormValues): P2PProviderDto => ({
      ...data,
      payoutConfig: convertPayoutConfig(data.payoutConfig),
    }),
    [convertPayoutConfig],
  );

  const onBeforeUpdateMutation = useCallback(
    ({
      id,
      data,
    }: {
      id: string;
      data: P2PProviderFormValues;
    }): { id: string; data: P2PProviderDto } => ({
      id,
      data: omit(
        { ...data, payoutConfig: convertPayoutConfig(data.payoutConfig) },
        ['assetCurrencyId'],
      ),
    }),
    [convertPayoutConfig],
  );

  return (
    <EntityDetailsPage
      title={title}
      id={id!}
      api={p2pProvidersApi}
      apiAsRole={false}
      listUrl={listUrl}
      initialValues={initialValues}
      validationSchema={validationSchema}
      queryKey={QueryKey.P2PProviders}
      formClassName="tw-max-w-md"
      initialValuesSelect={initialValuesSelect}
      onBeforeCreateMutation={onBeforeCreateMutation}
      onBeforeUpdateMutation={onBeforeUpdateMutation}
      invalidateOnSave
    >
      {({
        formik,
        queryResult,
      }: EntityDetailsPageChildrenProps<P2PProviderDto, P2PProvider>) => (
        <Fragment>
          <Typography variant="h5">{t('details.common_details')}</Typography>
          <FormikTextField label={t('fields.name')} name="name" required />
          <FormikSelect
            label={t('fields.type')}
            name="type"
            options={typeOptions}
            disabled={!!queryResult.data}
            required
          />
          <FormikNumericField
            label={t('fields.withdrawal_fee')}
            allowNegative={false}
            decimalScale={0}
            name="withdrawalFee"
            suffix={formatCurrencySymbol(formik.values.assetCurrencyId)}
            fullWidth
            required
          />
          <FormikNumericField
            label={t('fields.max_balance')}
            allowNegative={false}
            decimalScale={0}
            defaultValue={0}
            name="maxBalance"
            suffix={formatCurrencySymbol(formik.values.assetCurrencyId)}
            fullWidth
          />
          <FormikSelect
            label={t('fields.fiatCurrencyId')}
            name="fiatCurrencyId"
            options={fiatCurrenciesOptions}
            onChange={() => handleFiatCurrencyChange(formik)}
            required
          />
          <FormikSelect
            label={t('fields.assetCurrencyId')}
            name="assetCurrencyId"
            options={assetCurrenciesOptions}
            onChange={() => handleAssetCurrencyChange(formik)}
            disabled={!!queryResult.data}
            required
          />
          {formik.values.assetCurrencyId && (
            <FormikSelect
              label={t('fields.currency_exchange')}
              helperText={t('fields.currency_exchange_description')}
              name="currencyExchangeId"
              required={
                !!formik.values.assetCurrencyId &&
                defaultAssetCurrency?.id !== formik.values.assetCurrencyId
              }
              noneOption
              options={getCurrencyExchangeOptions({
                fiatCurrencyId: formik.values.fiatCurrencyId,
                assetCurrencyId: formik.values.assetCurrencyId,
              })}
            />
          )}

          {formik.values.assetCurrencyId &&
            formik.values.assetCurrencyId !== defaultAssetCurrency?.id && (
              <FormikSelect
                label={t('fields.asset_currency_exchange')}
                name="assetCurrencyExchangeId"
                required
                helperText={t(
                  'fields.withdrawal_currency_exchange_description',
                )}
                options={getAssetCurrencyExchangeOptions(
                  formik.values.assetCurrencyId,
                )}
                noneOption
              />
            )}

          <CommentField
            label={t('fields.comment')}
            commentedAt={queryResult.data?.commentedAt}
            commentedBy={queryResult.data?.commentedBy}
          />

          {isProviderAgentsEnabled && (
            <AgentsFormFields
              formError={formik.errors['agents'] as string | undefined}
              agents={formik.values.agents}
              users={queryResultTraders.data}
              headerClassName="tw-text-base tw-font-semibold"
            />
          )}

          <P2PProviderPayinConfig
            providerType={formik.values.type}
            assetCurrencyId={formik.values.assetCurrencyId}
            fiatCurrencyId={formik.values.fiatCurrencyId}
            onTradeMethodsChange={(event) =>
              handlePayinTradeMethodsChange(event, formik)
            }
            isNew={isNew}
          />

          <P2PProviderPayoutConfig
            providerType={formik.values.type}
            assetCurrencyId={formik.values.assetCurrencyId}
            fiatCurrencyId={formik.values.fiatCurrencyId}
            onTradeMethodsChange={(event) =>
              handlePayoutTradeMethodsChange(event, formik)
            }
            isNew={isNew}
          />

          <P2PProviderPayoutLimits />
        </Fragment>
      )}
    </EntityDetailsPage>
  );
};
